import {
  Box,
  Button,
  ButtonGroup,
  type ButtonProps,
  Flex,
  HStack,
  Text,
  useClipboard,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  ExpenseBudgetItemTypeEnum as ExpenseBudgetItemTypeEnumApollo,
  useCompleteExpenseBudgetItemMutation,
  useReopenExpenseBudgetItemMutation,
} from "@src/__generated__/graphql";
import {
  type ExpenseBudgetItem,
  ExpenseBudgetItemTypeEnum,
  ExpenseStatusEnum,
} from "@src/__generated__/urql-graphql";
import { ButtonLink, ModalConfirm } from "@src/components/ui-kit";
import { CircleTickIcon, Icon } from "@src/components/ui-kit/Icon";
import { ModalCompleteExpense } from "@src/components/widgets/Modals/ModalCompleteExpense";
import { onError } from "@src/utils/apollo";
import { currency, HUNDRED_PERCENT, percent } from "@src/utils/formatters";
import { useStore as useAppStore, useScrollToHashID } from "@src/utils/hooks";
import { observer, useLocalObservable } from "mobx-react-lite";
import { useRouter } from "next/router";
import { type FC, Fragment, type FunctionComponent, useEffect } from "react";
import type { ExpenseBudgetItemModel } from "../../models";
import { ProgressBar } from "../../ProgressBar";
import type { ExpenseBarSection } from "../../ProgressBar/Expenses";
import { useStore } from "../../Store";
import InvoicedButton from "../InvoicedButton";
import ItemTitle, { type ItemTitleProps } from "../ItemTitle";
import Uninvoiced from "../Uninvoiced";
import Row from "./Row";
import StatsBox from "./StatsBox";

type ExpenseItemProps = {
  item: ExpenseBudgetItemModel;
  visibleBudgetItemIds: string[];
  profitText?: string;
} & Pick<ItemTitleProps, "tabIndex">;

type TExpenseItemRowProps = {
  label: string;
  amount: number;
  percent?: number;
  section?: ExpenseBarSection;
  hoveredSection?: ExpenseBarSection;
  onSectionHover?: (section: ExpenseBarSection) => void;
  redPercentIfNegative?: boolean;
};
type TUnapprovedExpensesButtonProps = {
  expenseId: ExpenseBudgetItem["id"];
} & ButtonProps;

const ExpenseItemRow: FC<TExpenseItemRowProps> = ({
  label,
  amount,
  percent: _percent,
  section,
  hoveredSection,
  onSectionHover,
  redPercentIfNegative,
}) => {
  return (
    <HStack
      flexWrap="wrap"
      onMouseEnter={() => {
        if (!section) return;
        onSectionHover?.(section);
      }}
      onMouseLeave={() => {
        onSectionHover?.(undefined);
      }}
      spacing="0"
    >
      <Text
        fontWeight={
          !!hoveredSection && section === hoveredSection ? "bold" : undefined
        }
      >
        {label}&nbsp;
      </Text>
      <Text
        color={redPercentIfNegative && amount < 0 ? "red.500" : undefined}
        fontWeight={
          !!hoveredSection && section === hoveredSection ? "bold" : "medium"
        }
      >
        {currency.formatByWorkspace(amount)}
        {(!!_percent || _percent === 0) && (
          <Fragment>&nbsp;({percent.format(_percent)})</Fragment>
        )}
      </Text>
    </HStack>
  );
};

export const UnapprovedExpensesLinkButton: FC<
  TUnapprovedExpensesButtonProps
> = ({ expenseId, ...props }) => {
  const { UIStore } = useAppStore();
  const { query } = useRouter();
  const { onCopy } = useClipboard(
    `${location.origin}/projects/detail?id=${query.id}#${expenseId}`,
  );

  return (
    <Button
      fontSize="xs"
      href={{
        pathname: "/projects/detail",
        query: {
          id: query.id,
        },
        hash: expenseId,
      }}
      onClick={() => {
        onCopy();
        UIStore.toast({
          title: t`Link copied to clipboard`,
          status: "info",
          isClosable: true,
        });
      }}
      size="sm"
      variant="outline"
      {...props}
    >
      <Trans>Copy link to unapproved expenses</Trans>
    </Button>
  );
};

export const ExpenseItem: FunctionComponent<ExpenseItemProps> = observer(
  function ExpenseItem({ item: i, tabIndex, visibleBudgetItemIds }) {
    const { workspaceStore } = useAppStore();
    const store = useStore();
    const state = useLocalObservable(() => ({
      showButtons: false,
      section: undefined as ExpenseBarSection,
      handleSectionHover(section: ExpenseBarSection) {
        this.section = section;
      },
    }));
    const { hash } = location;
    const idFromHash = hash.replace("#", "");
    const completeModal = useDisclosure();
    const confirmModal = useDisclosure();

    useScrollToHashID(idFromHash === i.id);

    useEffect(() => {
      if (idFromHash === i.id) {
        completeModal.onOpen();
      }
    }, [idFromHash]);

    const [complete, { loading: completeLoading }] =
      useCompleteExpenseBudgetItemMutation({
        onCompleted() {
          store.refetchGraph?.();
          store.refetchProject?.();
        },
        ...onError(),
      });

    const [reopen, { loading: reopenLoading }] =
      useReopenExpenseBudgetItemMutation({
        onCompleted() {
          store.refetchGraph?.();
          store.refetchProject?.();
        },
        ...onError(),
      });

    const handleCloseExpense = () => {
      if (
        workspaceStore.settings
          ?.can_close_expense_budget_item_with_unapproved_expenses ||
        i.expense_statuses.every(
          (status) => status === ExpenseStatusEnum.Approved,
        )
      ) {
        complete({ variables: { id: i.id } });
      } else {
        confirmModal.onOpen();
      }
    };

    return (
      <Box
        bg="teal.50"
        borderBottom="1px"
        borderBottomColor="gray.300"
        role="group"
      >
        {/* <div
         onMouseEnter={() => {
           state.showButtons = true;
         }}
         onMouseLeave={() => {
           state.showButtons = false;
         }}
       > */}
        <Row id={i.id}>
          <VStack
            alignItems="flex-start"
            justify="center"
            w="35%"
            mr="1rem"
            pr="4"
          >
            <HStack w="full" spacing="2">
              <ItemTitle item={i} tabIndex={tabIndex} />
              {!!i.completed_at && <CircleTickIcon />}
            </HStack>
            <VStack display="block" w="full" spacing="4">
              <ProgressBar.Expenses
                stats={i.stats}
                section={state.section}
                isCompleted={!!i.completed_at}
                onSectionHover={state.handleSectionHover}
              />
            </VStack>
          </VStack>

          <HStack
            justify={store.project?.isTimeAndMaterials ? "center" : undefined}
            w="50%"
            p="4"
            spacing="2"
          >
            {!store.project?.isTimeAndMaterials && (
              <StatsBox title={t`Budgeted (Expenses)`}>
                <ExpenseItemRow
                  label={t`Total price (with commission):`}
                  amount={i.stats.total_budget}
                />
                <ExpenseItemRow
                  label={t`Client price:`}
                  amount={i.stats.client_price}
                />
                <ExpenseItemRow
                  label={t`Buy price:`}
                  amount={i.stats.planned_expenses}
                />
                <ExpenseItemRow
                  label={t`Profit estimate:`}
                  amount={i.stats.planned_profit}
                  percent={
                    (i.stats.planned_profit / i.stats.total_budget) *
                    HUNDRED_PERCENT
                  }
                  redPercentIfNegative
                  section="profit"
                  hoveredSection={state.section}
                  onSectionHover={(section) => {
                    if (i.completed_at) return;
                    state.handleSectionHover(section);
                  }}
                />

                <PlannedExpensesButton
                  item={i}
                  visibleBudgetItemIds={visibleBudgetItemIds}
                />
              </StatsBox>
            )}

            <Flex
              flex={store.project?.isTimeAndMaterials ? undefined : "0.75"}
              w={store.project?.isTimeAndMaterials ? "fit-content" : undefined}
            >
              <StatsBox title={t`Actual (Expenses)`}>
                <p className="flex items-center space-x-3">
                  <Button
                    colorScheme="blue"
                    onClick={completeModal.onOpen}
                    size="sm"
                    variant="link"
                  >
                    <ExpenseItemRow
                      label={t`Expenses received:`}
                      amount={i.stats.real_expenses}
                      percent={i.stats.real_expenses_percent}
                      section="received"
                      hoveredSection={state.section}
                      onSectionHover={state.handleSectionHover}
                    />
                  </Button>
                  {Boolean(i.completed_at) && (
                    <span
                      style={{ height: 22, width: 22 }}
                      className="inline-block"
                    >
                      <CircleTickIcon />
                    </span>
                  )}
                </p>
                {i.completed_at ? (
                  <ExpenseItemRow
                    label={t`Final Profit:`}
                    amount={i.stats.profit}
                    percent={i.stats.profit_percent}
                    redPercentIfNegative
                    section="final_profit"
                    hoveredSection={state.section}
                    onSectionHover={state.handleSectionHover}
                  />
                ) : (
                  <ExpenseItemRow
                    label={t`Actual Profit:`}
                    amount={i.stats.actual_profit}
                    percent={i.stats.profit_percent}
                    redPercentIfNegative
                  />
                )}

                {store.project?.isTimeAndMaterials && (
                  <PlannedExpensesButton
                    item={i}
                    visibleBudgetItemIds={visibleBudgetItemIds}
                  />
                )}

                {/* <Collapse in={state.showButtons}> */}
                <ButtonGroup
                  opacity="0"
                  _groupHover={{
                    opacity: "1",
                  }}
                  transition="all"
                  size="xs"
                  variant="outline"
                >
                  <ButtonLink
                    href={{
                      pathname: "/expenses/create",
                      query: {
                        brandId: store.project?.brand.id,
                        projectId: store.project?.id,
                        budgetItemId:
                          i.id +
                          "-" +
                          ExpenseBudgetItemTypeEnum.ExpenseBudgetItem,
                      },
                    }}
                  >
                    <Trans>+ Add expense</Trans>
                  </ButtonLink>
                  {i.completed_at ? (
                    <Button
                      colorScheme="red"
                      isLoading={reopenLoading}
                      onClick={() => reopen({ variables: { id: i.id } })}
                    >
                      <Trans>Re-open item</Trans>
                    </Button>
                  ) : (
                    <Button
                      isLoading={completeLoading}
                      onClick={handleCloseExpense}
                    >
                      <Trans>Close</Trans>
                    </Button>
                  )}
                </ButtonGroup>
                {/* </Collapse> */}
              </StatsBox>
            </Flex>
          </HStack>

          <HStack w="25%" pl="4">
            <StatsBox title={t`Invoiced`}>
              <InvoicedButton item={i} />
            </StatsBox>
            <StatsBox title={t`Uninvoiced`}>
              <Uninvoiced
                value={i.stats.uninvoiced}
                isProBono={!store.project?.billable}
              />
            </StatsBox>
          </HStack>

          <ModalCompleteExpense
            id={i.id}
            {...completeModal}
            projectId={store.project?.id}
            onAction={() => {
              store.refetchGraph?.();
              store.refetchProject?.();
            }}
          />
          <ModalConfirm
            {...confirmModal}
            onConfirm={() => {
              confirmModal.onClose();
              completeModal.onOpen();
            }}
            confirmBtnTitle={t`Show expenses`}
            heading={t`Unapproved expenses`}
            cancelBtnTitle={t`Close`}
          >
            <VStack align="start" spacing="4">
              <Text>
                <Trans>
                  This budget item isn't approved in some expenses. Please
                  contact your CFO with link provided below to approve them.
                </Trans>
              </Text>
              <UnapprovedExpensesLinkButton
                expenseId={i.id}
                alignSelf="center"
                variant="subtle"
              />
            </VStack>
          </ModalConfirm>
        </Row>
        {/* </div> */}
      </Box>
    );
  },
);

type PlannedExpensesButtonProps = Pick<
  ExpenseItemProps,
  "item" | "visibleBudgetItemIds"
>;
const PlannedExpensesButton = observer(function PlannedExpensesButton({
  item,
  visibleBudgetItemIds,
}: PlannedExpensesButtonProps) {
  const store = useStore();

  return (
    <Button
      alignSelf="flex-start"
      colorScheme="blue"
      onClick={() => {
        store.plannedExpensesModalStore.modalState.onOpen({
          item: {
            id: item.id,
            title: item.title,
            budgetItemType: ExpenseBudgetItemTypeEnumApollo.ExpenseBudgetItem,
            targetClientAmount: item.stats.client_price,
            targetBuyPriceAmount: item.stats.planned_expenses,
          },
          visibleBudgetItemIds,
          onSaved: ({ budgetItemIds, hasPlannedExpenses }) => {
            store.updateExpenseBudgetItemsHasPlannedExpenses(
              budgetItemIds,
              hasPlannedExpenses,
            );
          },
        });
      }}
      rightIcon={
        item.hasPlannedExpenses ? (
          <Icon boxSize="4" color="green.500" name="check-circle" />
        ) : undefined
      }
      size="xs"
      variant="link"
    >
      <Trans>Planned expenses</Trans>
    </Button>
  );
});
